import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import useSWRInfinite from 'swr/infinite';
import getConfig from 'next/config';

import { FeatureFlags } from 'featureFlags/constants';
import { fetcher } from 'shared/utils/swrDataFetcher';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useLocation } from 'shared/hooks/useLocation';
import { requestService } from 'shared/utils/services/requestService';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { useBigBuyBarDesktopView } from 'productTemplate/hooks/useBigBuyBarDesktopView';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { Update2022VariantGrid } from 'productTemplate/components/buyBar/Update2022VariantGrid';
import { IProductSearch } from 'shared/types/ProductSearch';
import { IMetaData } from 'shared/types/MetaData';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { Loader } from 'shared/components/Loader';

interface IVariantPriceListItemGridProps {
  variantType: VARIANT_KEYS.NEW_WITH_DEFECTS | 'special';
  onGridLoad: () => void;
}

const fetchData = (url) => requestService({ url, method: 'GET' });

export const VariantPriceListItemGrid = ({
  variantType,
  onGridLoad,
}: IVariantPriceListItemGridProps) => {
  const { size } = useStoreSelector((state) => state.pdpState);
  const { setBuyBarSizeLarge } = useBigBuyBarDesktopView();
  const { id, slug } = useStoreSelector((state) => state.pdp.productTemplate);
  const { selectedCountryCode }: { selectedCountryCode: string } = useLocation();

  const { publicRuntimeConfig } = getConfig();
  const { SPIKE_KEY } = publicRuntimeConfig;

  const productsRef = useRef<Array<{ metadata?: IMetaData; products: IProductSearch[] }>>();
  const isSpikeMitigateEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SPIKE_MITIGATION);

  let queryParams = null;
  if (variantType === VARIANT_KEYS.NEW_WITH_DEFECTS) {
    queryParams = `?productTemplateId=${id}&shoeCondition=${SHOE_CONDITIONS.NEW}&size=${size}&withDefects=true&countryCode=${selectedCountryCode}`;
  }
  if (variantType === 'special') {
    queryParams = `?productTemplateId=${id}&shoeCondition=${SHOE_CONDITIONS.NEW}&size=${size}&sortBy=size&withDefects=false&countryCode=${selectedCountryCode}`;
  }

  const {
    data,
    error,
    isLoading,
    setSize,
    size: page,
  } = useSWRInfinite(
    (_, prevData) => {
      if (prevData && !prevData?.metadata?.nextPage) return null;

      const searchUrl = [
        '/web-api/v1/products/search',
        queryParams,
        prevData?.metadata?.nextPage ? `&page=${prevData?.metadata?.nextPage as string}` : '',
      ].join('');

      return isSpikeMitigateEnabled ? { url: searchUrl, key: SPIKE_KEY } : searchUrl;
    },
    isSpikeMitigateEnabled ? fetcher : fetchData,
    { fallbackData: productsRef.current ? productsRef.current : [] },
  );

  const isLoadingInitialData = (!data && !error) || isLoading;
  const hasMoreData = Boolean(data?.[page - 1]?.metadata?.nextPage);
  const isLoadingMore = page > 0 && data && typeof data[page - 1] === 'undefined';
  const products = data?.flatMap((_data) => _data?.products);
  // As of today, the grid has 4 products max per row, so...
  const hasTwoOrMoreRows = products?.length > 4;

  if (data !== undefined && productsRef) {
    productsRef.current = data;
  }

  const observer = useRef();
  const shouldLoadMore = useIsElementInViewport(observer);

  useEffect(() => {
    if (shouldLoadMore && hasMoreData) {
      void setSize(page + 1);
    }
  }, [shouldLoadMore, setSize]);

  useEffect(() => {
    onGridLoad();
  }, []);

  useEffect(() => {
    if (data) {
      sendTrackingEvent('PDP_GRID_VIEW', {
        condition_category: variantType,
        product_count: products?.length,
        product_template_slug: slug,
        size,
      });
    }
  }, [data, size, slug, variantType]);

  useEffect(() => {
    setBuyBarSizeLarge(hasTwoOrMoreRows);
    return () => setBuyBarSizeLarge(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTwoOrMoreRows]);

  return (
    <>
      <VariantPriceListItemGrid.Root>
        <td colSpan={4}>
          <Update2022VariantGrid variants={products} isLoading={isLoadingInitialData} />
        </td>
      </VariantPriceListItemGrid.Root>
      {isLoadingMore && (
        <td colSpan={4}>
          <Loader
            className="loader"
            data-qa="variant_price_list_item_grid_loader"
            style={{ marginBlock: '10px', marginInline: 'auto' }}
          />
        </td>
      )}
      <div ref={observer} />
    </>
  );
};

VariantPriceListItemGrid.Root = styled.tr`
  height: 145px;
`;
